import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client/react';
import ChartPanel from './components/chart-card';
import ReviewCards from './components/review-cards';
import { COMPONENT_NAME as REVIEW_CARD_COMPONENT_NAME } from './components/review-cards/current-card';
import ReviewHistoryGrid from './components/review-history-grid';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import Loading from '@components-clean/core/loading/loading';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { MonitoringToolkitContext } from '@pages-clean/central-monitoring/monitoring-toolkit/context';
import { useBoolean } from '@hooks/index';
import { GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION } from '@queries/central-monitoring';
import {
    getMonitoringToolkitConfig,
    getMonitoringToolkitConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import DatasetReviewsApi from '@apis/bff-api/central-monitoring/dataset-reviews';
import './index.scss';

interface IProps {
    studyRef: string;
    canEdit: boolean;
    datasetId?: number;
}

const COMPONENT_NAMES = [REVIEW_CARD_COMPONENT_NAME];

const StudyDatasetReview = ({ studyRef, canEdit, datasetId }: IProps) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.siteDatasetReview',
    });
    const { bottomPanelRef } = useContext(MonitoringToolkitContext);
    const [selectedDataset, setSelectedDataset] = useState<CM2Dataset | undefined>();
    const [selectedComparisonDataset, setSelectedComparisonDataset] = useState<CM2Dataset | undefined>();

    const { data: datasets, isLoading: isLoadingDatasets } = DatasetApi.useGetActive(studyRef);

    const sortedDatasets = (datasets ?? []).map(formatDatasetDates).sort(sortDatasetByDatesDesc);

    const { data: previousDatasetIds } = DatasetReviewsApi.useGetPreviousReviewedDatasetIds(
        studyRef,
        selectedDataset?.id,
    );

    const previousDatasets = sortedDatasets.filter((dataset) => previousDatasetIds?.includes(dataset.id!));
    const allowKRIReviewsState = useBoolean(false);

    useEffect(() => {
        if (!datasets) return;

        setSelectedDataset(
            sortedDatasets.length > 0
                ? (sortedDatasets.find((d) => d.id === datasetId) ?? sortedDatasets[0])
                : undefined,
        );
    }, [datasets]);

    // If the updated datasets list no longer contains the currently selected dataset, clear down selection
    useEffect(() => {
        if (!previousDatasets.some((dataset) => dataset.id === selectedComparisonDataset?.id)) {
            setSelectedComparisonDataset(undefined);
        }
    }, [previousDatasets]);

    const [loadConfig] = useLazyQuery<getMonitoringToolkitConfig, getMonitoringToolkitConfigVariables>(
        GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION,
    );

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } })
            .then((response) =>
                allowKRIReviewsState.setValue(response.getMonitoringToolkitConfig?.allowKRIReviews ?? false),
            )
            .catch((err) => window.console.error(err));
    }, [studyRef]);

    useEffect(() => {
        setSelectedComparisonDataset(undefined);
    }, [selectedDataset]);

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAMES,
    });

    if (isLoadingDatasets) {
        return <Loading />;
    }

    if (!datasets || datasets.length === 0) {
        return <span>Unable to load datasets.</span>;
    }

    return (
        <div className="study-dataset-review">
            <Card
                data-testid="topbar-dropdowns"
                bodyClassName="topbar-content"
                className="topbar-styling topbar-splitters"
            >
                <div className="selector-area">
                    <BasicDropDown
                        label={`${t('selectors.reviewDate.label')}`}
                        monitoringToolkitTopbar
                        data-testid="review-date"
                        data={sortedDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={sortedDatasets.find((d) => d.id === selectedDataset?.id)}
                        onChange={async (e) => {
                            const confirmed = await confirmLossOfChanges();
                            if (!confirmed) {
                                return;
                            }

                            const dataset = e.value as CM2Dataset;
                            setSelectedDataset(dataset);
                        }}
                        disabled={!selectedDataset}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear={false}
                        cardHeader
                    />
                    <BasicDropDown
                        label={`${t('selectors.lastReviewDate.label')}`}
                        monitoringToolkitTopbar
                        data-testid="last-review-date"
                        data={previousDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={previousDatasets.find((d) => d.id === selectedComparisonDataset?.id) ?? null}
                        onChange={(e) => {
                            const dataset = e.value as CM2Dataset;

                            setSelectedComparisonDataset(dataset);
                        }}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear={false}
                        cardHeader
                    />
                </div>
            </Card>

            <ReviewCards
                studyRef={studyRef}
                dataset={selectedDataset}
                comparisonDataset={selectedComparisonDataset}
                canEdit={canEdit}
            />

            <ChartPanel
                studyRef={studyRef}
                datasetId={selectedDataset?.id}
                allowKRIReviewsState={allowKRIReviewsState.value}
            />

            <ReviewHistoryGrid studyRef={studyRef} />
        </div>
    );
};

export default StudyDatasetReview;
